import { Component, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PERIOD_KINDS,
  PeriodBar,
  PeriodKind,
  ShelterablePeriod,
  monthPart,
  periodBars,
} from '../../../../../../@commons/@services/front/period-bars.util';
import { monthName } from '../../../../../../@commons/@services/front/seed-timing.util';
import { StorageService } from '../../../../../../@commons/@services/front/storage.service';

interface Track {
  kind: PeriodKind;
  bars: PeriodBar[];
}

/**
 * Les barres de période d'une culture (calendrier, guide) : une piste par
 * nature (à l'abri, en place, plantation, récolte), seulement celles qui ont
 * une période. Toucher une barre donne ses dates en mots, « Plantation :
 * mi-mai à mi-juin chez toi » (JAR-05). En liste serrée (`dense`), les
 * barres ne se touchent pas une à une.
 */
@Component({
  selector: 'vw-calendar-period-line',
  imports: [TranslatePipe],
  templateUrl: './calendar-period-line.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './calendar-period-line.component.scss',
})
export class CalendarPeriodLineComponent implements OnChanges {
  @Input() periods: ShelterablePeriod[] = [];
  /** Mois mis en avant (1 à 12) : un trait vertical le marque ; nul, aucun. */
  @Input() month: number | null = null;
  /** Les initiales des mois au-dessus des pistes (guide) ; le calendrier les porte une fois en tête de liste. */
  @Input() showMonths = false;
  /**
   * Barres fines sur la grille des mois, sans bulle (liste du calendrier) :
   * la ligne entière se touche et ouvre la fiche, où les barres donnent leurs dates.
   */
  @Input() dense = false;

  tracks: Track[] = [];
  /** La barre touchée, dont la bulle est ouverte. */
  activeBar: PeriodBar | null = null;
  readonly monthNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  constructor(
    private translate: TranslateService,
    private storageService: StorageService,
  ) {}

  ngOnChanges() {
    const bars = periodBars(this.periods);
    this.tracks = PERIOD_KINDS.map((kind) => ({ kind, bars: bars.filter((bar) => bar.kind === kind) })).filter(
      (track) => track.bars.length > 0,
    );
    this.activeBar = null;
  }

  /** Position du trait du mois : au milieu du mois choisi. */
  get markerLeft(): number | null {
    return this.month ? ((this.month - 0.5) / 12) * 100 : null;
  }

  /** La bulle au-dessus de la barre touchée, sans déborder de l'écran. */
  get tipLeft(): number {
    if (!this.activeBar) return 50;
    const center = ((this.activeBar.start + this.activeBar.end) / 2 / 12) * 100;
    return Math.min(75, Math.max(25, center));
  }

  toggle(bar: PeriodBar, event: Event): void {
    event.stopPropagation();
    this.activeBar = this.activeBar === bar ? null : bar;
  }

  /** « Plantation : mi-mai à mi-juin chez toi ». */
  tipFor(bar: PeriodBar): string {
    const lang = this.storageService.getCurrentLanguage();
    const word = (month: number, day: number, isEnd: boolean): string => {
      const name = monthName(month, lang);
      const part = monthPart(day, isEnd);
      return part ? this.translate.instant('calendar.month-part.' + part, { month: name }) : name;
    };
    return this.translate.instant('calendar.bars.tip', {
      kind: this.translate.instant('calendar.bars.kind.' + bar.kind),
      from: word(bar.period.startMonth, bar.period.startDay, false),
      to: word(bar.period.endMonth, bar.period.endDay, true),
    });
  }
}
